import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import styled from "styled-components";
import { ConsentContext } from "./layout";
import Button from "./button";
import { _ } from "../utils/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { StaticImage } from "gatsby-plugin-image";

const InstagramFeedWrapper = styled.div`
  display: grid;
  position: relative;
  width: 100%;
  gap: 1.5em;
  grid-template-columns: repeat(2, 1fr);
  box-sizing: border-box;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const InstagramFeedItem = styled.a`
  display: block;
  position: relative;
  width: 100%;
  height: fit-content;
  padding-bottom: 2rem;
  text-decoration: none;
  color: var(--color-black);
  opacity: ${({ $dim }) => ($dim ? "0.25" : "1")};
  transition: opacity 0.4s linear;
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
      .icon {
        animation: bounceRight 0.6s ease-in-out infinite;
      }
    }
  }

  &.no-consent > * {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const InstagramFeedImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
`;

const InstagramConsent = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
`;

const InstagramConsentDialog = styled.div`
  background-color: var(--color-white);
  color: var(--color-black);
  padding: 2rem;
  margin: 1em;
`;

const InstagramConsentButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1em;
`;

const InstagramText = styled.div`
  text-decoration: none;
  display: flex;
  justify-content: space-between;
  padding-inline: 0.5rem;
`;

const InstagramFeed = () => {
  const {
    wp: { instagramFeed },
  } = useStaticQuery(graphql`
    query InstagramFeed {
      wp {
        instagramFeed {
          caption
          mediaUrl
          url
        }
      }
    }
  `);
  const { consent, updateConsent } = React.useContext(ConsentContext);
  const [showConsent, setShowConsent] = React.useState(undefined);

  React.useEffect(() => {
    if (
      showConsent === undefined &&
      consent?.instagram !== undefined &&
      updateConsent
    ) {
      setShowConsent(!consent.instagram);
    } else if (showConsent === true && consent?.instagram === true) {
      setShowConsent(false);
    }
  }, [consent?.instagram, showConsent]);

  return (
    <InstagramFeedWrapper>
      {instagramFeed?.map((item) => (
        <InstagramFeedItem
          href={item.url}
          key={item.url}
          title={item.caption}
          rel="nofollow noopener noreferrer"
          target="_blank"
          $dim={showConsent}
        >
          {consent?.instagram ? (
            <>
              <InstagramFeedImage
                src={item.mediaUrl}
                alt={item.caption}
                loading="lazy"
              />
              <InstagramText>
                {item.caption}
                <FontAwesomeIcon className="icon" icon={faArrowRight} />
              </InstagramText>
            </>
          ) : (
            <>
              <StaticImage
                src="../images/no-image.webp"
                loading="lazy"
                style={{ filter: "blur(3px)" }}
              />
              <InstagramText>
                {item.caption}
                <FontAwesomeIcon className="icon" icon={faArrowRight} />
              </InstagramText>
            </>
          )}
        </InstagramFeedItem>
      ))}
      {showConsent && (
        <InstagramConsent>
          <InstagramConsentDialog>
            <p>{_("Trenger samtykke for å laste inn bilder fra Instagram.")}</p>
            <InstagramConsentButtons>
              <Button
                onClick={() => {
                  updateConsent({ instagram: true });
                  setShowConsent(false);
                }}
                padding="14px 64px"
              >
                {_("Last inn bilder fra Instagram")}
              </Button>

              <Button padding="14px 64px" onClick={() => setShowConsent(false)}>
                {_("Fortsett uten bilder")}
              </Button>
            </InstagramConsentButtons>
          </InstagramConsentDialog>
        </InstagramConsent>
      )}
    </InstagramFeedWrapper>
  );
};

export default InstagramFeed;
